import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PageLayout from "../../layouts/PageLayout";
import { formatOrderMoney, loadSellerOrder } from "./sellerOrderUtils";

const imageUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const baseUrl = process.env.REACT_APP_IMG_URL || "";
    return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

export default function StoreSellerOrderViewPage() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [order, setOrder] = useState(location.state?.order || null);
    const [loading, setLoading] = useState(!location.state?.order);
    const [error, setError] = useState("");

    useEffect(() => {
        if (order && String(order.id) === String(id)) return undefined;

        let active = true;
        setLoading(true);
        loadSellerOrder(id)
            .then((loadedOrder) => {
                if (active) setOrder(loadedOrder);
            })
            .catch((requestError) => {
                if (active) setError(requestError.response?.data?.message || requestError.message || "Could not load this order.");
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [id, order]);

    if (loading) {
        return <PageLayout><div className="text-center py-5"><Spinner animation="border" /></div></PageLayout>;
    }

    if (!order) {
        return (
            <PageLayout>
                <Alert variant="danger">{error || "Order details are not available."}</Alert>
                <Button variant="secondary" onClick={() => navigate(-1)}>Back to orders</Button>
            </PageLayout>
        );
    }

    const buyer = order.buyer || {};
    const products = Array.isArray(order.products) ? order.products : [];
    const statusVariant = order.delivery_status === "Delivered"
        ? "success"
        : order.delivery_status === "Returned"
            ? "danger"
            : order.delivery_status === "Shipped"
                ? "primary"
                : "warning";

    return (
        <PageLayout>
            <div className="mc-card mb-4">
                <div className="mc-breadcrumb d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h3 className="mc-breadcrumb-title">Order {order.order_number || `#${order.id}`}</h3>
                        <p className="text-muted mb-0">Order details and fulfillment information</p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button as={Link} to={`/seller-store-orders/${order.id}/edit`} state={{ order }}>Edit order</Button>
                        <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
                    </div>
                </div>
            </div>

            <Row className="g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Products</h5>
                            <Badge bg={statusVariant}>{order.delivery_status || "Pending"}</Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table responsive className="align-middle mb-0">
                                    <thead><tr><th>Product</th><th>Code</th><th>Qty</th><th>Unit price</th><th>Total</th></tr></thead>
                                    <tbody>
                                        {products.map((line) => (
                                            <tr key={line.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        {line.product?.card_img && (
                                                            <img
                                                                src={imageUrl(line.product.card_img)}
                                                                alt=""
                                                                style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6 }}
                                                            />
                                                        )}
                                                        <span className="fw-semibold">{line.product?.title || "Product"}</span>
                                                    </div>
                                                </td>
                                                <td>{line.product?.product_code || "—"}</td>
                                                <td>{line.quantity ?? "—"}</td>
                                                <td>{formatOrderMoney(line.unit_price)}</td>
                                                <td>{formatOrderMoney(Number(line.unit_price || 0) * Number(line.quantity || 0))}</td>
                                            </tr>
                                        ))}
                                        {!products.length && <tr><td colSpan={5} className="text-center py-4">No product details found.</td></tr>}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white py-3"><h5 className="mb-0">Delivery</h5></Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col sm={6}><span className="text-muted d-block">Courier company</span><strong>{order.courier_company || "—"}</strong></Col>
                                <Col sm={6}><span className="text-muted d-block">Tracking number</span><strong>{order.tracking_number || "—"}</strong></Col>
                                <Col sm={6}><span className="text-muted d-block">Delivery status</span><strong>{order.delivery_status || "Pending"}</strong></Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3"><h5 className="mb-0">Buyer</h5></Card.Header>
                        <Card.Body>
                            <p className="fw-semibold mb-1">{buyer.name || "—"}</p>
                            <p className="text-muted mb-1">{buyer.email || "—"}</p>
                            <p className="text-muted mb-3">{buyer.phone || "—"}</p>
                            <div className="text-muted">
                                {[buyer.address, buyer.city, buyer.country].filter(Boolean).join(", ") || "No address provided"}
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white py-3"><h5 className="mb-0">Payment summary</h5></Card.Header>
                        <Card.Body>
                            <div className="d-flex justify-content-between mb-2"><span className="text-muted">Payment status</span><strong>{order.payment_status || "—"}</strong></div>
                            {/* Dollars only, and only this seller's items. */}
                            <div className="d-flex justify-content-between mb-2"><span className="text-muted">Your items</span><strong>{formatOrderMoney(order.items_total_usd)}</strong></div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Platform fee{products[0]?.fee_percent !== undefined ? ` (${Number(products[0].fee_percent)}%)` : ""}</span>
                                <strong className="text-danger">− {formatOrderMoney(order.fee_total_usd)}</strong>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between fs-5"><span>You receive</span><strong className="text-success">{formatOrderMoney(order.payout_total_usd)}</strong></div>
                            {products[0]?.payout?.status_label && (
                                <small className="d-block text-muted mt-2">Payout: {products[0].payout.status_label}</small>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}
